import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AirIcon from "@mui/icons-material/Air";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { StatCard } from "../../../../../shared/components/charts";
import { DashboardCard, CohortGuard } from "../../../../../shared/components";
import { useMetricsStats } from "../../../../../shared/hooks";
import type { MetricsFilters } from "../../../../../shared/api/types";
import { useBiometricsStats } from "../hooks";

interface BiometricsStatsColumnProps {
  filterParams: MetricsFilters;
  isCentralAdmin: boolean;
  isAnonymised: boolean;
  stacked?: boolean;
}

const getChipColor = (ragColor: string | undefined): "success" | "warning" | "error" | "default" => {
  switch (ragColor) {
    case "green":
      return "success";
    case "amber":
      return "warning";
    case "red":
      return "error";
    default:
      return "default";
  }
};

/**
 * Renders Biometrics & Vitals' four stat cards + the Pace of Aging card (with
 * per-vital RAG chips) for a single filter scope. Safe to render once per group.
 */
export const BiometricsStatsColumn: React.FC<BiometricsStatsColumnProps> = ({
  filterParams,
  isCentralAdmin,
  isAnonymised,
  stacked = false,
}) => {
  const theme = useTheme();
  const statsQuery = useBiometricsStats({
    ...filterParams,
    metrics: ["respiratory_rate", "spo2", "pace_of_aging", "temperature", "resting_heart_rate", "hrv"],
  });
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;
  const stats = statsQuery.data?.data;

  const statCards = useMemo(() => {
    if (!stats) return [];
    return [
      { metric: stats.resting_heart_rate, icon: FavoriteIcon },
      { metric: stats.hrv, icon: MonitorHeartIcon },
      { metric: stats.respiratory_rate, icon: AirIcon },
      { metric: stats.spo2, icon: WaterDropIcon },
    ].filter((card) => card.metric);
  }, [stats]);

  const isLoading = statsQuery.isLoading;
  const statColSize = stacked ? { xs: 12, sm: 6 } : { xs: 12, sm: 6, md: 3 };

  const vitalRows: Array<{ label: string; icon: React.ReactNode; key: string }> = [
    { label: "Respiration", icon: <AirIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />, key: "respiratory_rate" },
    { label: "SpO2", icon: <WaterDropIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />, key: "spo2" },
    { label: "Resting HR", icon: <FavoriteIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />, key: "resting_heart_rate" },
    { label: "HRV", icon: <MonitorHeartIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />, key: "hrv" },
    { label: "Temperature", icon: <ThermostatIcon fontSize="medium" sx={{ color: theme.palette.primary.main }} />, key: "temperature" },
  ];

  const paceChange = stats?.pace_of_aging?.change?.trim() || "";
  const paceIsNeg = paceChange.startsWith("-");
  const paceIsPos = paceChange.startsWith("+");
  const paceColor = paceIsNeg
    ? theme.palette.error.main
    : paceIsPos
    ? theme.palette.success.main
    : theme.palette.text.secondary;
  const PaceIcon = paceIsNeg ? TrendingDownIcon : paceIsPos ? TrendingUpIcon : TrendingFlatIcon;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {isLoading
          ? [1, 2, 3, 4].map((i) => (
              <Grid size={statColSize} key={i}>
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              </Grid>
            ))
          : statCards.map((stat, index) => (
              <Grid size={statColSize} key={index}>
                <StatCard metric={stat.metric} icon={stat.icon} />
              </Grid>
            ))}
      </Grid>

      <DashboardCard title="Average Pace of Aging" subtitle="" expandable={false}>
        <CohortGuard
          userCount={activeUserCount}
          bypass={isCentralAdmin || !isAnonymised}
          loading={statsQuery.isLoading}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 260 }}>
              <CircularProgress />
            </Box>
          ) : stats?.pace_of_aging ? (
            <>
              <Box sx={{ mb: 3, display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, fontSize: "2rem" }}>
                  {stats.pace_of_aging.value}x
                </Typography>
                {paceChange && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: paceColor }}>
                    <Typography component="span" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      {paceChange}
                    </Typography>
                    <PaceIcon sx={{ fontSize: "0.875rem", color: paceColor }} />
                  </Box>
                )}
              </Box>
              <Stack spacing={2}>
                {vitalRows.map((row) => (
                  <Box key={row.key} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {row.icon}
                    <Typography variant="body1" sx={{ flex: 1 }}>
                      {row.label}
                    </Typography>
                    <Chip
                      label={(stats as Record<string, { rag_label?: string }>)[row.key]?.rag_label || "N/A"}
                      size="small"
                      color={getChipColor(
                        (stats as Record<string, { rag_color?: string }>)[row.key]?.rag_color
                      )}
                    />
                  </Box>
                ))}
              </Stack>
            </>
          ) : (
            <Alert severity="info">No Pace of Aging data</Alert>
          )}
        </CohortGuard>
      </DashboardCard>
    </Box>
  );
};
